import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthStore } from '@vantage-web/core-data';

/**
 * Root landing guard for the unified single-app entry point.
 *
 * Reads the authenticated user's role from {@link AuthStore} and redirects to
 * the matching role-area prefix. Always returns a `UrlTree` (the root path
 * renders nothing itself), so there is no flicker before the redirect.
 *
 * Corporate (NBD) staff land on `/oip` alongside territory staff: the prototype
 * models "corporate" as a *role* inside the single staff app (capability-gated
 * nav + national data-scoping), not a separate area — there is no `/corp`
 * landing. Unauthenticated users (or an unrecognized role) are sent to login.
 *
 * Only used by the unified `vantage` app — the four standalone apps each mount
 * a single area at their own root and never reach this guard.
 */
export const roleLandingGuard: CanActivateFn = () => {
  const store = inject(AuthStore);
  const router = inject(Router);

  if (!store.isAuthenticated()) {
    return router.parseUrl('/auth/login');
  }
  // Corporate + OIP (territory) staff share the unified `/oip` staff app.
  if (store.isCorpUser() || store.isOipUser()) {
    return router.parseUrl('/oip');
  }
  if (store.isSubUser()) {
    return router.parseUrl('/sub');
  }
  if (store.isClientUser()) {
    return router.parseUrl('/client');
  }
  return router.parseUrl('/auth/login');
};
